import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { resolveMediaUrl } from "../lib/media";
import { postUrl } from "../lib/urls";

const variants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const FEATURED_SLUGS = [
  "draming",
  "the-allure-of-northern-italy-and-switzerland",
  "sugar-sweet-mauritius",
  "how-we-met-madonna",
];

export default function FeaturedDestinations() {
  const { getPost } = useContent();
  const featured = FEATURED_SLUGS.map(getPost).filter(Boolean);
  if (featured.length < 4) return null;

  return (
    <section
      id="featured"
      data-testid="featured-destinations"
      className="relative bg-[#FDFBF7] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 lg:mb-20">
          <div>
            <p className="kicker">Featured · Chapter One</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight mt-5 max-w-3xl text-stone-900">
              A year of slow travel,
              <br />
              <em className="italic">one cliff at a time.</em>
            </h2>
          </div>
          <Link
            to="/destinations"
            data-testid="featured-view-all"
            className="inline-flex self-start md:self-end link-underline text-[13px] tracking-[0.18em] uppercase font-semibold text-stone-900"
          >
            All destinations &nbsp;→
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
          <motion.div variants={variants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} custom={0} className="md:col-span-5 md:row-span-2">
            <PostCardFeature post={featured[0]} size="xl" />
          </motion.div>
          <motion.div variants={variants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} custom={1} className="md:col-span-7">
            <PostCardFeature post={featured[1]} size="wide" />
          </motion.div>
          <motion.div variants={variants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} custom={2} className="md:col-span-4">
            <PostCardFeature post={featured[2]} size="square" />
          </motion.div>
          <motion.div variants={variants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} custom={3} className="md:col-span-3">
            <PostCardFeature post={featured[3]} size="tall" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function PostCardFeature({ post, size = "square" }) {
  if (!post) return null;
  const ratio =
    size === "xl" ? "aspect-[3/4]" :
    size === "wide" ? "aspect-[16/10]" :
    size === "tall" ? "aspect-[3/4]" :
    "aspect-[4/5]";

  return (
    <Link to={postUrl(post)} data-testid={`feature-card-${post.slug}`} className="group block">
      <div className={`relative ${ratio} overflow-hidden bg-stone-200`}>
        <img
          src={resolveMediaUrl(post.hero)}
          alt={post.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-transparent" />
        <div className="absolute top-5 left-5 text-white/90 text-[10px] tracking-[0.25em] uppercase font-semibold">
          {post.kicker}
        </div>
      </div>
      <div className="mt-5">
        <p className="kicker text-stone-500">{post.kicker}</p>
        <h3 className="font-serif text-2xl sm:text-3xl font-normal leading-snug mt-2 text-stone-900 group-hover:text-[#9c4122] transition-colors">
          {post.title}
        </h3>
        {(size === "xl" || size === "wide") && (
          <p className="mt-3 text-stone-600 text-[15px] leading-relaxed max-w-xl">{post.excerpt}</p>
        )}
      </div>
    </Link>
  );
}
